/**
 * @file Renders the investment description list and the linked investor account drawer.
 */
import { useRef } from "react";
import { Descriptions } from "antd";

import { InvestorDrawer } from "@components/Investor";

import getItems from "./helpers/getItems";
import type { Account, Investment, DrawerHandle, ReadInvestment } from "../../types";

import "./InvestmentDescriptions.css";

const { Item } = Descriptions;

const LABEL_STYLE = { width: "30%" };

type Props = {
  account: Account;
  investment: Investment;
  readInvestment: ReadInvestment;
  openInvestmentProfileUpdateDrawer: () => void;
  openInvestmentAccreditationUpdateModal: () => void;
};

/**
 * Renders the investment's description items and the account drawer they can open.
 */
const InvestmentDescriptions = ({
  account,
  investment,
  readInvestment,
  openInvestmentProfileUpdateDrawer,
  openInvestmentAccreditationUpdateModal,
}: Props) => {
  const accountDrawer = useRef<DrawerHandle>({});

  const { id, investorAccountId } = investment;

  /**
   * Opens the investor account drawer for the investment's account.
   */
  const openAccountDrawer = () =>
    accountDrawer.current.open(investorAccountId);

  /**
   * Reloads the investment after its linked account is updated.
   */
  const onAfterAccountUpdate = () =>
    readInvestment(id);

  const items = getItems({
    account,
    investment,
    openAccountDrawer,
    onAfterAccountUpdate,
    openInvestmentProfileUpdateDrawer,
    openInvestmentAccreditationUpdateModal,
  });

  return (
    <>
      <Descriptions
        bordered
        size="small"
        column={1}
        className="compact black"
        labelStyle={LABEL_STYLE}
      >
        {
          items.map(({ label, value }, key) =>
            <Item
              key={key}
              label={label}
            >
              {value}
            </Item>,
          )
        }
      </Descriptions>

      <InvestorDrawer
        drawer={accountDrawer}
        onAfterUpdate={onAfterAccountUpdate}
      />
    </>
  );
};

export default InvestmentDescriptions;
